// The Microsoft flow lands here with the tokens (or an error) in the fragment.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { advertisingGranted, measurementGranted } from '../analytics';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, Spinner } from '../components/ui';
import { parseCallbackHash } from '../services/callback';
import { storeTokens } from '../services/refreshAuth';
import { useSession } from '../session';
import type { SsoErrorCode } from '../types';

export function CallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reload } = useSession();
  const [error, setError] = useState<SsoErrorCode | null>(null);

  useEffect(() => {
    const outcome = parseCallbackHash(window.location.hash);
    try {
      window.history.replaceState(null, '', window.location.pathname); // never leave tokens in the history
    } catch {
      /* ignore */
    }
    if (outcome.type === 'tokens') {
      storeTokens(outcome.tokens);
      void reload().then(() => {
        // A sign-in that has just created the account (the API's `new_account=1`) is a sign-up, told by the API rather
        // than guessed from the account's age: signing in again, or with Microsoft after signing up by email, is not one.
        // The conversion then goes as from the signup form, with the visitor's consent, written in full so that it can be
        // read in the built bundle (an invitation is not one for LinkedIn).
        if (outcome.newAccount) {
          const invited = outcome.returnTo.startsWith('/invite/');
          if (measurementGranted() && typeof window.gtag === 'function') {
            window.gtag('event', 'sign_up', { method: 'microsoft', signup_type: invited ? 'invitation' : 'direct' });
          }
          if (advertisingGranted() && !invited && typeof window.lintrk === 'function') {
            window.lintrk('track', { conversion_id: 31855170 });
          }
        }
        navigate(outcome.returnTo, { replace: true });
      });
    } else {
      setError(outcome.type === 'error' ? outcome.error : 'sso_failed');
    }
  }, [navigate, reload]);

  return (
    <PublicLayout title={error ? t('callback.failed') : t('callback.title')}>
      {error ? (
        <>
          <Alert kind="error">{t(`auth.sso.${error}`)}</Alert>
          {/* A refused "Link Microsoft" leaves the account signed in as it was: back to it, not to the sign-in. */}
          <Link to={error === 'link_mismatch' ? '/' : '/login'}>
            <Button className="w-full" variant="secondary">
              {error === 'link_mismatch' ? t('callback.backToAccount') : t('callback.retry')}
            </Button>
          </Link>
        </>
      ) : (
        <Spinner />
      )}
    </PublicLayout>
  );
}
